import React, {useMemo, useState} from 'react';
import {connect, useSelector} from 'react-redux';
import moment from 'moment';
import {useNavigate} from 'react-router-dom';
import {
  DashboardLayout,
  EmptyState,
  Loader,
  PageLimit,
  Pagination,
  TableLayout,
  SecondaryButton,
} from '../../../components';
import * as Actions from '../../../store/actions';
import {fetchCases} from '../../../requests/queries/flows';
import {shortenLargeNumbers} from '../../../helpers/shortenLargeNumbers';
import {getStatusTextAndStyles} from '../../../helpers/getStatusTextAndStyles';
import {OverviewCards} from '../../../components/OverviewCards';
import {buildTableCardData} from '../../../helpers/buildTableCardData';
import {eye} from '../../../assets/images/images';
import FilterV2 from 'components/FilterV2';
import PageNumber from 'components/PageNumber';
import {useFlowsLogic} from '../Flows/hooks/useFlows';
import {Constants} from '../../../helpers';
import {useGatedQuery} from '../Flows/hooks/useGatedQuery';

function PendingCases() {
  const {
    auth: {userDetails},
  } = useSelector(state => state);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const {flows} = useFlowsLogic({isFilter: true});

  const projectFilters = flows?.flows?.map(flow => ({
    label: flow.name,
    value: flow.id,
  }));

  const filterOptions = useMemo(
    () => [
      {
        key: 'project_id',
        label: 'Flows',
        type: 'select',
        multiSelect: false,
        options: projectFilters,
      },
    ],
    [projectFilters],
  );

  const handleFilterChange = values => {
    setFilterValues(values);
    setCurrentPage(1);
  };

  const params = useMemo(
    () => ({
      company_id: userDetails?.company?.id,
      limit,
      page: currentPage,
      status: 'Pending',
      ...(filterValues.dateRange && {
        date_from: filterValues.dateRange.start,
        date_to: filterValues.dateRange.end,
      }),
      project_id: filterValues.project_id,
    }),
    [currentPage, limit, userDetails?.company?.id, filterValues],
  );

  const {data: casesData, isLoading} = useGatedQuery(
    ['pending-flow-cases', {params}],
    () => fetchCases({params}),
  );

  const CASES = useMemo(
    () => (!isLoading && casesData && casesData?.rows) || [],
    [casesData, isLoading],
  );

  const pageClick = selected => {
    setCurrentPage(selected);
  };

  const tableCardData = buildTableCardData({
    count: casesData?.count,
  });

  const toggleFilterVisibility = () => {
    setShowFilter(prevShowFilter => !prevShowFilter);
  };

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Pending Cases</p>
      }
    >
      <>
        <div className="flex items-center justify-end py-2 flex-wrap gap-3 mx-[-32px] px-8 border border-transparent border-b-[#E4E3EC]">
          <SecondaryButton
            buttonText="Filter"
            onClick={toggleFilterVisibility}
          />
        </div>

        <div className={`filter-slide-container ${showFilter ? 'open' : ''}`}>
          <FilterV2
            filters={filterOptions}
            onFilterChange={handleFilterChange}
          />
        </div>

        <OverviewCards
          cardData={tableCardData}
          overviewLoading={isLoading}
          className={`grid grid-cols-1 gap-2 lg:gap-4 flex-wrap my-5`}
        />

        <TableLayout negativeMargins>
          <thead className="font-semibold uppercase">
            <tr className="bg-white80">
              <th className="pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>Case Id</span>
                </div>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>Amount</span>
                </div>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>STATUS</span>
                </div>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>date created</span>
                </div>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap"></th>
            </tr>
          </thead>

          <tbody className="text-sm text-body">
            <tr>
              <td colSpan={8}>
                {isLoading ? (
                  <Loader height={45} />
                ) : (CASES && CASES?.length === 0) || CASES === null ? (
                  <EmptyState
                    height={55}
                    body="You currently don't have any cases, create flow to get started"
                    buttonText={'Create a flow'}
                    onClick={() => navigate(Constants.PAGES_URL.FLOWS)}
                  />
                ) : null}
              </td>
            </tr>
          </tbody>

          <tbody className="text-sm bg-white">
            {CASES?.length !== 0 &&
              CASES?.map((_case, i) => (
                <tr className="border-[0.5px] border-[#E4E3EC]" key={i}>
                  <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-body whitespace-nowrap capitalize">
                    {`${_case.id.toUpperCase()}` || '-'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {shortenLargeNumbers(_case.transaction?.amount, true, {
                      startAt: 'B',
                    }) || '-'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    <div
                      className={`uppercase py-1 px-2 text-xs font-medium ${
                        getStatusTextAndStyles(_case?.status?.toLowerCase())
                          ?.solid
                      } rounded-full flex items-center w-fit`}
                    >
                      <span className="-mb-[2px]">{_case.status || '-'}</span>
                    </div>
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {moment(_case?.createdAt).format('Do MMM YYYY h:m a')}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    <button
                      onClick={() =>
                        navigate(
                          `/flows/cases/${userDetails?.company?.id}/details/${_case.id}`,
                          {
                            state: {
                              status: _case.status,
                              createdAt: moment(_case?.createdAt).format(
                                'Do MMM YYYY h:m a',
                              ),
                              from: 'pending-cases', // Indicate source route
                            },
                          },
                        )
                      }
                      className="h-4 aspect-square"
                    >
                      <img src={eye} alt="" />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </TableLayout>
      </>

      {!isLoading && CASES?.length !== 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
          <PageNumber
            currentPage={currentPage}
            limit={limit}
            totalCount={casesData?.count || 0}
          />

          <div className="flex items-center gap-5">
            <PageLimit
              onLimitChange={setLimit}
              total={casesData?.count}
              length={CASES?.length}
              limit={limit}
            />
            <Pagination
              total={casesData && Math.ceil(casesData?.count / limit)}
              current={+currentPage}
              onPageChange={activePage => {
                localStorage.setItem('casesPageNumber', String(activePage));
                pageClick(activePage);
              }}
            />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default connect(state => state, Actions)(PendingCases);
